/**
 * @aw/i18n @ v1.0.0
 * 
 * Copyright © 2023 American Well.
 * All rights reserved.
 * 
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @aw/public-config @ v1.0.0
 * 
 * Copyright © 2022 American Well.
 * All rights reserved.
 * 
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

const defaultConfig$1 = {
  // prod
  serviceUrl: 'https://api.cvg01.amwell.systems/public-config/v1/config',
  // stg
  // serviceUrl: 'https://api.stg01.amwell.systems/public-config/v1/config'
  // dev
  // serviceUrl: 'https://api.dev.amwell.systems/public-config/v1/config'
};

/**
 * Set or Get local configuration for the `loader`
 *
 * @param {String|Object} obj retrieve a single key property (string) or set configuration as a k/v pair
 * @returns {*} As setter, returns the whole configuration object. As getter, returns the value at the requested key
 */
const config$1 = (obj) => {
  if (typeof obj === 'string') {
    return defaultConfig$1[obj];
  }
  Object.assign(defaultConfig$1, obj);
  return defaultConfig$1;
};

var config_1 = config$1;

const config$2 = config_1;

/**
 * Convert kwArgs to business-logic ordered list of `layers`
 *
 * @private
 * @param {*} args the kwArgs for single and batch entries
 * @returns {Object} suitable for passing as a request body to the bento-config service
 */
const single = (args = {}) => {
  const {
    base, product, tenant, ehr, serviceLine, providerGroup, mix, and, root,
  } = args;
  return {
    root: root || 'layers',
    layers: [base, product, tenant, ehr, serviceLine, providerGroup, ...(and || [])].filter((a) => a),
    mix,
  };
};

/**
 * Utilize the service `batch` support, running multiple configuration fetchs concurrently
 *
 * TODO: how to handle global `mix` (per-request `mix` happens, but global mixing does not)
 *
 * @param  {...object} requests ordered params, one for each of the batched request configs
 * @returns {Promise<object[]>} an array of results aligning to the batch order with discreet configs therein
 */
const batch = (...requests) => ({ batch: requests.map(single) });

/**
 * Execute the request to the bento-config service
 *
 * @param {object} body the body of the payload
 * @returns
 */
const execute = (body) => fetch(config$2('serviceUrl'), {
  method: 'POST',
  mode: 'cors',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(body),
}).then((r) => r.json());

/**
 * Fetch a bento configuration from the `bento-config` service
 *
 * @param {object} args optional arguments
 * @param {string} [args.base] base level object to use. `video` is a good choice for MVP console
 * @param {string} [args.product] optional product id to select as a layer
 * @param {string} [args.tenant] a optional `tenantId` to select as a layer
 * @param {string} [args.ehr] an optional `ehrId` to select as a layer
 * @param {string} [args.serviceLine] an optional `serviceLine` to select as a layer
 * @param {object} [args.mix] an object to mix over the resulting merged layer selection
 * @param {string[]} [args.and] an array of strings, to be compiled after all other layer selections are chosen
 * @param {string} [args.root] an optional override for the base path to use for layer data.
 * @returns {Promise<object>} a promise resolving to the resulting bento manifest
 */
const getConfig = (args = {}) => execute(single(args));

/**
 * Exported version of the configuration getter/setter, for overrides.
 */
getConfig.config = config$2;
getConfig.batch = batch;

var src = getConfig;

const defaultConfig = {
  // prod
  serviceUrl: 'https://api.cvg01.amwell.systems/public-config/v1/config',
  // stg
  // serviceUrl: 'https://api.stg01.amwell.systems/public-config/v1/config'
  // dev
  // serviceUrl: 'https://api.dev.amwell.systems/public-config/v1/config',
  mergeResults: true,
};

/**
 * Set or Get local configuration for the `loader`
 *
 * @param {String|Object} obj retrieve a single key property (string) or set configuration as a k/v pair
 * @returns {*} As setter, returns the whole configuration object. As getter, returns the value at the requested key
 */
const config = (obj) => {
  if (typeof obj === 'string') {
    return defaultConfig[obj];
  }
  Object.assign(defaultConfig, obj);
  src.config({
    serviceUrl: defaultConfig.serviceUrl,
  });
  return defaultConfig;
};

const handleResults = (arr) => {
  if (defaultConfig.mergeResults) {
    return arr.reduce((acc, item) => Object.assign(acc, item), {});
  }
  return arr.length === 0 ? arr[0] : arr;
};

let cache = {};

/**
 * Fetch translationі from the public config
 *
 * @param {string|string[]} files Filename or list of filenames.
 * @param {string} lang optional language code. Default: en
 * @returns {Promise<object|object[]>} a promise resolving to the resulting bento manifest
 */

const getTranslations = (files, lang = 'en') => {
  const requests = typeof files === 'string' ? [files] : files;
  return new Promise((resolve, reject) => {
    Promise.all(requests.map((req) => {
      const base = `${req}-${lang}`;
      if (!cache[base]) {
        cache[base] = src({
          root: 'i18n',
          base,
        });
      }
      return cache[base];
    }))
      .then(((res) => resolve(handleResults(res))))
      .catch((error) => reject(error));
  });
};

const clearCache = (val) => {
  if (val) {
    delete cache[val];
    return cache;
  }
  cache = {};
  return cache;
};

/**
 * Exported version of the configuration getter/setter, for overrides.
 */
module.exports = {
  getTranslations,
  config,
  clearCache,
};
